import { trpc } from "@/Utils/trpc";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { type KpiNumberData, KpiNumbersView } from "@/components/v2/KpiNumbers";
import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { PortfolioPageContext } from "../../context";
import { getKpiNumbersData } from "./getKpiNumbersData";

export const KPINumbersPortfolioPage: React.FC = () => {
  const { t } = useTrans();
  const { timeFilter, comparisonTimeFilter, datePreset, selectedBuildingIds } =
    useContext(PortfolioPageContext);

  const { data: currentAggregatesData, isLoading: isLoadingCurrentAggregates } =
    trpc.dwh.terminals.getAggregates.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalIds: selectedBuildingIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminals",
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedBuildingIds?.toString(),
          ].toString(),
        enabled: !!timeFilter && !!selectedBuildingIds,
      },
    );

  const {
    data: comparisonAggregatesData,
    isLoading: isLoadingComparisonAggregates,
  } = trpc.dwh.terminals.getAggregates.useQuery(
    {
      ...(comparisonTimeFilter || defaultTimeFilter),
      terminalIds: selectedBuildingIds || [],
    },
    {
      queryKeyHashFn: () =>
        [
          "terminals",
          comparisonTimeFilter?.startTimeISO,
          comparisonTimeFilter?.endTimeISO,
          selectedBuildingIds?.toString(),
        ].toString(),
      enabled: !!comparisonTimeFilter && !!selectedBuildingIds,
    },
  );

  const { data: terminalsData, isLoading: isLoadingTerminals } =
    trpc.dwh.terminals.get.useQuery(timeFilter || defaultTimeFilter, {
      queryKeyHashFn: () =>
        [
          "terminals",
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
        ].toString(),
      enabled: !!timeFilter,
    });

  const filteredBuildings = useMemo(
    () =>
      (terminalsData || []).filter((t) =>
        selectedBuildingIds?.includes(t.terminalId),
      ),
    [terminalsData, selectedBuildingIds],
  );

  const showLoadingView = useMemo(
    () =>
      !timeFilter ||
      isLoadingCurrentAggregates ||
      isLoadingComparisonAggregates ||
      isLoadingTerminals,
    [
      timeFilter,
      isLoadingCurrentAggregates,
      isLoadingComparisonAggregates,
      isLoadingTerminals,
    ],
  );

  const numbersData: KpiNumberData[] = useMemo(
    () =>
      getKpiNumbersData({
        currentData: currentAggregatesData,
        comparisonData: comparisonAggregatesData,
        datePreset,
        presetHasComparison: !!comparisonTimeFilter,
        terminalsData: filteredBuildings,
        t,
      }),
    [
      currentAggregatesData,
      comparisonAggregatesData,
      comparisonTimeFilter,
      datePreset,
      t,
      filteredBuildings,
    ],
  );

  return (
    <KpiNumbersView
      showLoadingView={showLoadingView}
      numbersData={numbersData}
      gridClassNames="grid-cols-2 min-[621px]:grid-cols-3 min-[1001px]:grid-cols-6"
    />
  );
};
